import {
  DETAIL_LEVEL,
  ENTHUSIASM_LEVEL,
  FORMALITY_LEVEL,
  PROMPT_TYPE,
  PROMPT_USE_CASE,
  Option,
  LandingPageOption,
  CrmData,
  GenerateTextParams,
  LandingPageType,
  GetQuickshareParams,
} from '../types';
import {
  removeHTMLTags,
  generateSimplifiedCrmCode,
  prepareWebsiteOverlayOptions,
  prepareTemplateLandingPageOptions,
  prepareLinksetCTAOptions,
  formatGeneratedText,
} from '../utils';
import React, { ReactNode, useEffect, useState } from 'react';
import { useGetTemplatesQuery } from '../queries/useGetTepmlatesQuery';
import { useGetPromptsLeftCount } from '../queries/useGetPromptsLeftCount';
import { useGetQuickshareDetails } from '../queries/useGetQuickShareDetails';
import { useGetOverlays } from '../queries/useGetOverlays';
import { useGetLinkSetsQuery } from '../queries/useGetLinksQuery';
import { useAuth } from '../../provider/auth';
import { useGenerateTextStream } from '../queries/useGenerateTextStream';
import { useGenerateText } from '../queries/useGenerateText';

interface IAiContext {
  promptType: PROMPT_TYPE;
  promptUseCase: PROMPT_USE_CASE;
  detailLevel: DETAIL_LEVEL;
  formalityLevel: FORMALITY_LEVEL;
  videoId: number;
  overlayOptions: Option[];
  selectedOverlayOption?: Option;
  templateOptions: LandingPageOption[];
  selectedTemplateOption?: LandingPageOption;
  linksetOptions: Option[];
  selectedLinksetOption?: Option;
  userInput: string;
  recipient: string;
  sender: string;
  senderCompany: string;
  vin: string;
  nextStep: string;
  additionalInfo: string;
  isLoading: boolean;
  promptsLeft: number;
  generatedText: string;
  baseText: string;
  crmData?: CrmData;
  isStreamingResponse: boolean;
  generatedThumnail: string;
  generatedUrl: string;
  params: GetQuickshareParams | undefined;
  changePromptUseCase: (useCase: PROMPT_USE_CASE) => void;
  changePromptType: (promptType: PROMPT_TYPE) => void;
  changeVin: (val: string) => void;
  changeVehicleTitle: (val: string) => void;
  changeSender: (val: string) => void;
  changeSenderCompany: (val: string) => void;
  changeRecipient: (val: string) => void;
  changeNextStep: (val: string) => void;
  changeGeneratedText: (val: string) => void;
  changeBaseText: (val: string) => void;
  changeAdditionalInfo: (val: string) => void;
  changeFormalityLevel: (val: FORMALITY_LEVEL) => void;
  changeEnthusiasmLevel: (val: ENTHUSIASM_LEVEL) => void;
  changeDetailLevel: (val: DETAIL_LEVEL) => void;
  changeSelectedTemplateOption: (val: LandingPageOption) => void;
  changeSelectedLinksetOption: (val: Option) => void;
  changeSelectedOverlayOption: (val: Option) => void;
  generateContentWithAi: () => void;
  handleAbort: () => void;
}

export const AiContext = React.createContext({} as IAiContext);

export type AiProviderProps = {
  children?: ReactNode;
  videoId?: number | string;
  defaultPromptType: PROMPT_TYPE;
  defaultPromptUseCase?: PROMPT_USE_CASE;
  userInput?: string;
  defaultVin?: string;
  defaultRecipient?: string;
};

export const AiProvider = ({
  children,
  videoId = 0,
  defaultPromptType,
  defaultPromptUseCase = PROMPT_USE_CASE.INITIAL_REACH_OUT,
  userInput = '',
  defaultVin = '',
  defaultRecipient = '',
}: AiProviderProps) => {
  const { userData } = useAuth();

  const { customer, isAutomotiveServiceRole } = userData;

  const [promptType, setPromptType] = useState(defaultPromptType);
  const [promptUseCase, setPromptUseCase] = useState(defaultPromptUseCase);
  const [baseText, setBaseText] = useState(removeHTMLTags(userInput) || '');
  const [generatedText, setGeneratedText] = useState(userInput || '');
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [generatedThumnail, setGeneratedThumnail] = useState('');
  const [vin, setVin] = useState(defaultVin);
  const [vehicleTitle, setVehicleTitle] = useState('');
  const [sender, setSender] = useState(userData.firstName || '');
  const [senderCompany, setSenderCompany] = useState(customer.business);
  const [nextStep, setNextStep] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [recipient, setRecipient] = useState(defaultRecipient);
  const [formalityLevel, setFormalityLevel] = useState(FORMALITY_LEVEL.MID);
  const [enthusiasmLevel, setEnthusiasmLevel] = useState(ENTHUSIASM_LEVEL.MID);
  const [detailLevel, setDetailLevel] = useState(DETAIL_LEVEL.MID);
  const [templateOptions, setTemplateOptions] = useState<LandingPageOption[]>(
    []
  );
  const [overlayOptions, setOverlayOptions] = useState<Option[]>([]);
  const [linksetOptions, setLinksetOptions] = useState<Option[]>([]);
  const [selectedTemplateOption, setSelectedTemplateOption] =
    useState<LandingPageOption>();
  const [selectedOverlayOption, setSelectedOverlayOption] = useState<Option>();
  const [selectedLinksetOption, setSelectedLinksetOption] = useState<Option>();
  const [isStreamingResponse, setIsStreamingResponse] = useState(false);
  const [abortController, setAbortController] =
    useState<AbortController | null>(null);

  const { data: promptsLeft = { count: 0 }, isLoading: isLoadingCount } =
    useGetPromptsLeftCount();

  const { mutateAsync: streamText, isLoading: isLoadingGenerateText } =
    useGenerateTextStream();

  const { mutateAsync: generateText, isLoading: isLoadingStreamText } =
    useGenerateText();

  const {
    data: crmData,
    isFetching: isGeneratingCrmData,
    changeQuickshareParams,
    params,
  } = useGetQuickshareDetails({
    videoId: Number(videoId) || 0,
    enabledOnMount: false,
  });

  const fetchShareSettingsData = promptType === PROMPT_TYPE.CRM_MESSAGE;

  const { isLoading: isLoadingOverlays, data: overlays } = useGetOverlays(
    {
      start: 0,
      limit: 100,
      search: '',
    },
    fetchShareSettingsData
  );

  const { isLoading: isLoadingTemplates, data: templates } =
    useGetTemplatesQuery(
      {
        start: 0,
        limit: 500,
        search: '',
      },
      fetchShareSettingsData
    );

  const { isLoading: isLoadingExitLinkSets, data: linksets } =
    useGetLinkSetsQuery(
      {
        start: 0,
        limit: 200,
        search: '',
      },
      fetchShareSettingsData
    );

  useEffect(() => {
    if (!overlays) {
      return;
    }
    const {
      autoSelectedOption: defaultOverlayOption,
      options: overlayOptions,
    } = prepareWebsiteOverlayOptions(overlays);
    setOverlayOptions(overlayOptions);
    if (defaultOverlayOption) {
      setSelectedOverlayOption(defaultOverlayOption);
    }
  }, [overlays]);

  const user = {
    isAutomotive: false,
    isAutomotiveSalesRole: false,
    isAutomotiveServiceRole: false,
  };

  useEffect(() => {
    if (!templates) {
      return;
    }
    const {
      autoSelectedOption: defaultTemplateOption,
      options: templateOptions,
    } = prepareTemplateLandingPageOptions({
      userData: user,
      templatesResponse: templates,
      sendVehicle: !!vin,
    });
    setTemplateOptions(templateOptions);
    setSelectedTemplateOption(defaultTemplateOption);
  }, [templates, vin]);

  useEffect(() => {
    if (!linksets) {
      return;
    }
    const {
      autoSelectedOption: defaultLinksetOption,
      options: linksetOptions,
    } = prepareLinksetCTAOptions({
      linksetsResponse: linksets,
    });
    setLinksetOptions(linksetOptions);
    if (defaultLinksetOption) {
      setSelectedLinksetOption(defaultLinksetOption);
    }
  }, [linksets]);

  useEffect(() => {
    if (
      !videoId ||
      promptType !== PROMPT_TYPE.CRM_MESSAGE ||
      !selectedTemplateOption
    ) {
      return;
    }
    changeQuickshareParams({
      templateId: parseInt(selectedTemplateOption.value.toString()),
      vin,
      linksetId: (selectedLinksetOption && selectedLinksetOption.value) || '-1',
      overlayId: (selectedOverlayOption && selectedOverlayOption.value) || '',
      useAutomotive:
        isAutomotiveServiceRole ||
        !!vin ||
        selectedTemplateOption.landingPageType === LandingPageType.AUTOMOTIVE,
    });
  }, [
    selectedTemplateOption,
    selectedOverlayOption,
    selectedLinksetOption,
    vin,
    promptType,
    videoId,
  ]);

  useEffect(() => {
    setPromptType(defaultPromptType);
  }, [defaultPromptType]);

  useEffect(() => {
    setPromptUseCase(defaultPromptUseCase);
  }, [defaultPromptUseCase]);

  useEffect(() => {
    const cleanText = removeHTMLTags(userInput);
    setGeneratedText(cleanText);
    setBaseText(cleanText);
  }, [userInput]);

  useEffect(() => {
    setRecipient(defaultRecipient);
  }, [defaultRecipient]);

  useEffect(() => {
    setVin(defaultVin);
  }, [defaultVin]);
  function extractUrls(str: string) {
    const regex = /https?:\/\/[^\s'"]+/g;
    const matches = str.match(regex);
    return matches || [];
  }
  useEffect(() => {
    const html = (crmData && crmData.data.html) || '';
    if (!html) {
      return;
    }
    const thumbnail = (crmData && crmData.data.thumbnail) || '';
    const url = (crmData && crmData.data.url) || '';

    setGeneratedThumnail(extractUrls(crmData.data.thumbnail)?.[0] || '');
    setGeneratedUrl(url);
    const crmCode = generateSimplifiedCrmCode(thumbnail, url, sender) || html;
    setGeneratedText(crmCode);
    setBaseText(crmCode);
  }, [crmData && crmData.data.html, sender]);

  const isLoading =
    isLoadingOverlays ||
    isLoadingTemplates ||
    isLoadingExitLinkSets ||
    isLoadingGenerateText ||
    isLoadingStreamText ||
    isGeneratingCrmData ||
    isLoadingCount;

  const changePromptUseCase = (useCase: PROMPT_USE_CASE) =>
    setPromptUseCase(useCase);
  const changePromptType = (promptType: PROMPT_TYPE) =>
    setPromptType(promptType);
  const changeVehicleTitle = (val: string) => setVehicleTitle(val);
  const changeVin = (val: string) => setVin(val);
  const changeSender = (val: string) => setSender(val);
  const changeSenderCompany = (val: string) => setSenderCompany(val);
  const changeRecipient = (val: string) => setRecipient(val);
  const changeNextStep = (val: string) => setNextStep(val);
  const changeBaseText = (val: string) => setBaseText(val);
  const changeGeneratedText = (val: string) => setGeneratedText(val);
  const changeAdditionalInfo = (val: string) => setAdditionalInfo(val);
  const changeFormalityLevel = (val: FORMALITY_LEVEL) => setFormalityLevel(val);
  const changeDetailLevel = (val: DETAIL_LEVEL) => setDetailLevel(val);
  const changeEnthusiasmLevel = (val: ENTHUSIASM_LEVEL) =>
    setEnthusiasmLevel(val);
  const changeSelectedTemplateOption = (option: LandingPageOption) =>
    setSelectedTemplateOption(option);
  const changeSelectedOverlayOption = (option: Option) =>
    setSelectedOverlayOption(option);
  const changeSelectedLinksetOption = (option: Option) =>
    setSelectedLinksetOption(option);

  const handleAbort = () => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
      setIsStreamingResponse(false);
    }
  };

  const streamContentWithAi = async () => {
    const data: GenerateTextParams = {
      baseText,
      promptUseCase,
      promptType,
      formalityLevel,
      enthusiasmLevel,
      detailLevel,
      vehicleTitle,
      vin,
      recipient,
      sender,
      senderCompany,
      callToAction: nextStep,
      additionalInfo,
      videoId,
    };
    setGeneratedText('');
    setIsStreamingResponse(true);
    try {
      const controller = new AbortController();
      setAbortController(controller); // Store the controller in the state
      const response = await streamText({ data, signal: controller.signal });
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      const loopRunner = true;

      while (loopRunner) {
        // Here we start reading the stream, until its done.
        const { value, done } = await reader.read();
        if (done) {
          break;
        }
        const decodedChunk = decoder.decode(value, { stream: true });
        setGeneratedText((prev) =>
          formatGeneratedText(prev + decodedChunk, promptType)
        );
      }
    } catch (error) {
      if ((error as unknown as { name: string })?.name === 'AbortError') {
        console.log('Fetch aborted');
      } else {
        console.log(error);
      }
    }
    setIsStreamingResponse(false);
  };

  const generateContentWithAi = async () => {
    const data: GenerateTextParams = {
      baseText,
      promptUseCase,
      promptType,
      formalityLevel,
      enthusiasmLevel,
      detailLevel,
      vehicleTitle,
      vin,
      recipient,
      sender,
      senderCompany,
      callToAction: nextStep,
      additionalInfo,
      videoId,
    };
    const { generatedText } = await generateText(data);
    setGeneratedText(generatedText);
  };

  const useStreaming = true; // in case we need to fallback in some cases to regular text generation

  const value: IAiContext = {
    videoId: Number(videoId) || 0,
    promptType,
    promptUseCase,
    userInput,
    isLoading,
    overlayOptions,
    templateOptions,
    selectedTemplateOption,
    selectedLinksetOption,
    selectedOverlayOption,
    changeSelectedTemplateOption,
    changeSelectedLinksetOption,
    changeSelectedOverlayOption,
    linksetOptions,
    changePromptUseCase,
    changePromptType,
    generateContentWithAi: useStreaming
      ? streamContentWithAi
      : generateContentWithAi,
    promptsLeft: (promptsLeft && promptsLeft.count) || 0,
    generatedText,
    baseText,
    detailLevel,
    crmData,
    changeVehicleTitle,
    changeVin,
    changeSender,
    sender,
    changeSenderCompany,
    senderCompany,
    changeRecipient,
    recipient,
    vin,
    changeNextStep,
    nextStep,
    changeFormalityLevel,
    formalityLevel,
    changeDetailLevel,
    changeEnthusiasmLevel,
    additionalInfo,
    changeAdditionalInfo,
    changeBaseText,
    changeGeneratedText,
    generatedThumnail,
    generatedUrl,
    isStreamingResponse,
    handleAbort,
    params,
  };

  return <AiContext.Provider value={value}>{children}</AiContext.Provider>;
};

export const useAI = () => {
  const context = React.useContext(AiContext);
  if (context === undefined) {
    throw new Error('useAI must be used within a AiProvider');
  }
  return context;
};
